import classNames from 'classnames'
import {useMemo} from 'react'
import * as React from 'react'

import {useAppSelector} from '../../../../../hooks/react-redux'
import useEventIdCallback from '../../../../../hooks/useEventIdCallback'
import {useIsAutodetectTimeZone} from '../../../../../hooks/useIsAutodetectTimeZone'
import {getBuild} from '../../../../../selectors'
import RouterLink from '../../../../common/RouterLink/RouterLink'
import {
  PipelinesTarget,
  PipelinesTargetContext,
} from '../../../../pages/PipelinesPages/contexts/targetContext'
import type {LogView} from '../../BuildLog.types'
import {getFullBuildLogLinkParams} from '../../BuildLog.utils'
import {BuildIdContext, TargetContext} from '../../FullBuildLog/FullBuildLog.context'
import {printMessageTime, printRelativeTime} from '../BuildLogMessages.utils'
import LogMessageMenu from '../LogMessageMenu/LogMessageMenu'

import styles from './LogMessageTime.css'

type Props = {
  id: number
  text: string
  expanded: boolean
  expandable: boolean
  timestamp: string
  serverTimestamp?: string
  onClick: (event: React.MouseEvent, id: number) => unknown
  toggleMessageSubtree: (index: number, expand: boolean) => unknown
  highlight?: boolean
  logView: LogView
  relativeTime?: boolean
}

function LogMessageTime({
  onClick,
  timestamp,
  serverTimestamp,
  id,
  text,
  highlight,
  logView,
  expanded,
  expandable,
  toggleMessageSubtree,
  relativeTime,
}: Props) {
  const buildId = React.useContext(BuildIdContext)
  const target = React.useContext(TargetContext)
  const pipelineTarget = React.useContext(PipelinesTargetContext)
  const handleClick = useEventIdCallback<React.MouseEvent, number>(onClick, id)
  const isAutodetectTimeZone = useIsAutodetectTimeZone()
  const {startDate} = useAppSelector(state => getBuild(state, buildId)) ?? {}

  const anchor = useMemo(
    () =>
      relativeTime ? (
        <span>
          {printRelativeTime(serverTimestamp ?? timestamp, startDate, isAutodetectTimeZone)}
        </span>
      ) : (
        <span>{printMessageTime(serverTimestamp ?? timestamp, isAutodetectTimeZone)}</span>
      ),
    [startDate, isAutodetectTimeZone, relativeTime, serverTimestamp, timestamp],
  )

  if (buildId == null) {
    return null
  }

  if (
    pipelineTarget === PipelinesTarget.JOB_DETAILS_POPUP ||
    pipelineTarget === PipelinesTarget.JOB_DETAILS_SIDEBAR
  ) {
    return <div className={classNames(styles.time)}>{anchor}</div>
  }

  return (
    <RouterLink
      className={classNames(styles.time, {
        [styles.highlight]: highlight,
      })}
      params={getFullBuildLogLinkParams({
        buildId,
        line: id,
        expandState: [id],
        target,
        expandAll: false,
        logView,
      })}
      onClick={handleClick}
      data-test-log-message-time
    >
      <LogMessageMenu
        id={id}
        expanded={expanded}
        expandable={expandable}
        toggleMessageSubtree={toggleMessageSubtree}
        text={text}
        timestamp={timestamp}
        serverTimestamp={serverTimestamp}
        anchor={anchor}
      />
    </RouterLink>
  )
}

export default React.memo(LogMessageTime)
